import { Box, Stack } from '@mui/system';
import {
  BookingStatus,
  DriverStatus,
  RideOfferType,
  ScheduleItemType,
  StyledTooltip,
  colors,
  formatDateTimeShortWithLocale,
  formatTimeWithLocale,
} from 'remote/global';
import { EventContentArg, SlotLabelContentArg } from '@fullcalendar/core';
import { Typography } from '@mui/material';
import { ColCellContentArg, ResourceLabelContentArg } from '@fullcalendar/resource';
import { Link } from 'react-router-dom';
import TransferIcon from 'assets/transfer-icon.svg?react';
import { TFunction } from 'i18next';

export function DataGridOverlay(title: string) {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      {title}
    </Stack>
  );
}

export function SchedulerOverlay({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.7)',
        position: 'absolute',
        left: 0,
        top: 0,
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        zIndex: 99,
      }}
    >
      {children}
    </Box>
  );
}

const getEventColor = (eventType: string) => {
  switch (eventType) {
    case DriverStatus.ACTIVE:
      return colors.activeGreen;
    case DriverStatus.AVAILABLE:
      return colors.uiBlue500;
    case DriverStatus.VACATION:
      return colors.gray1;
    case DriverStatus.SICKNESS:
      return colors.primaryBlue3;
    case DriverStatus.TIME_OFF:
      return colors.restYellow;
    default:
      return colors.busyRed;
  }
};

export const renderWorkScheduleEventContent = (eventContent: EventContentArg) => {
  const { itemType, itemTypeTranslated } = eventContent.event.extendedProps;

  return (
    <Box
      sx={{
        padding: '20px 12px',
        background: getEventColor(itemType),
        cursor: 'default',
        borderRadius: '6px',
      }}
    >
      <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 400, lineHeight: '18px' }}>
        {itemTypeTranslated}
      </Typography>
    </Box>
  );
};

export const renderWorkScheduleResourceContent = (resourceContent: ResourceLabelContentArg) => {
  const { title, extendedProps } = resourceContent.resource;
  return (
    <Box>
      <Typography
        sx={{ color: colors.uiBlue600, fontSize: '14px', fontWeight: 400, lineHeight: '20px' }}
        {...(extendedProps?.url && { component: Link, to: extendedProps.url })}
      >
        {title}
      </Typography>
    </Box>
  );
};

const getEventBorderColor = (eventType: string) => {
  switch (eventType) {
    case BookingStatus.NO_DRIVER:
      return colors.busyRed;
    case BookingStatus.DRIVER_ASSIGNED:
      return colors.restYellow;
    case BookingStatus.CONFIRMED:
      return '#9DD5F6';
    case BookingStatus.STARTED:
      return colors.uiBlue600;
    case BookingStatus.FINISHED:
      return colors.activeGreen;
    case BookingStatus.PAUSED:
      return colors.gray3;
    default:
      return colors.busyRed;
  }
};

function calculatePercentage(
  start: Date,
  end: Date,
  clientStartTime: Date,
  clientEndTime: Date
): { clientStartTimePercentage: number; clientEndTimePercentage: number } {
  // Calculate the time duration in milliseconds
  const totalDuration = end.getTime() - start.getTime();

  // Calculate the time duration for clientStartTime and clientEndTime
  const clientStartDuration = clientStartTime.getTime() - start.getTime();
  const clientEndDuration = clientEndTime.getTime() - start.getTime();

  // Calculate the percentage
  const clientStartTimePercentage = (clientStartDuration / totalDuration) * 100;
  const clientEndTimePercentage = (clientEndDuration / totalDuration) * 100;

  return {
    clientStartTimePercentage: Number(clientStartTimePercentage.toFixed(2)),
    clientEndTimePercentage: Number(clientEndTimePercentage.toFixed(2)),
  };
}

export const renderAvailabilityEventContent = (eventContent: EventContentArg, language: string) => {
  const startTime = formatTimeWithLocale(eventContent.event.start as Date, language);
  const endTime = formatTimeWithLocale(eventContent.event.end as Date, language);
  const { clientStartTime, clientEndTime } = eventContent.event.extendedProps;
  const { start, end } = eventContent.event;
  const startDateTime = formatDateTimeShortWithLocale(start, language);
  const endDateTime = formatDateTimeShortWithLocale(end, language);
  const bookingNumber = eventContent.event.title;
  const { bookingStatus, orderType, rideNumberTranslated, itemTypeTranslated } =
    eventContent.event.extendedProps;

  const timePercentages = calculatePercentage(
    start as Date,
    end as Date,
    new Date(clientStartTime),
    new Date(clientEndTime)
  );

  const isRideType = eventContent.event.extendedProps.itemType === ScheduleItemType.RIDE;
  const rideColor = getEventBorderColor(bookingStatus);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          borderRadius: '2px',
          position: 'relative',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '3px',
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
            backgroundColor: rideColor,
            zIndex: -1,
          }}
        />
        {isRideType && timePercentages.clientStartTimePercentage !== 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: `${timePercentages.clientStartTimePercentage}%`,
              height: '100%',
              opacity: '30%',
              backgroundColor: rideColor,
              zIndex: -1,
            }}
          />
        )}

        {isRideType && timePercentages.clientEndTimePercentage !== 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${timePercentages.clientEndTimePercentage}%`,
              bottom: 0,
              width: `calc(100% - ${timePercentages.clientEndTimePercentage}%)`,
              height: '100%',
              opacity: '30%',
              backgroundColor: rideColor,
              zIndex: -1,
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            left: isRideType ? `${timePercentages.clientStartTimePercentage}%` : 0,
            bottom: 0,
            width: isRideType
              ? `calc(100% - ${timePercentages.clientStartTimePercentage}% - calc(100% - ${timePercentages.clientEndTimePercentage}%))`
              : '100%',
            height: '100%',
            opacity: '60%',
            backgroundColor: rideColor,
            zIndex: -2,
          }}
        />
        <Typography
          sx={{
            color: colors.gray2,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 'auto',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            paddingLeft: '4px',
            transform: 'translateY(1px)', // We translate for the top bar offset so that the text is still visually in center
          }}
        >
          {bookingNumber ? `${startTime} - ${endTime}` : `${startDateTime} - ${endDateTime}`}
          {bookingNumber ? ` • #${bookingNumber}` : ` • ${itemTypeTranslated}`}
          {orderType === RideOfferType.RETURN_AS_TRANSFER && ` • ${rideNumberTranslated}`}
        </Typography>
      </Box>
      {orderType === RideOfferType.RETURN_AS_TRANSFER && (
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 18,
            height: 18,
            borderRadius: 18 / 2,
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          <TransferIcon />
        </Box>
      )}
    </Box>
  );
};

export const renderAvailabilityResourceContent = (resourceContent: ResourceLabelContentArg) => {
  const { title, extendedProps } = resourceContent.resource;
  const { subtitle, tooltip } = extendedProps ?? {};

  return (
    <StyledTooltip title={tooltip} placement='left'>
      <Box
        sx={{
          display: 'flex',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: colors.gray2,
        }}
      >
        <Typography
          sx={{
            color: colors.uiBlue600,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '18px',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
          {...(extendedProps?.url && { component: Link, to: extendedProps.url })}
        >
          {title}
          {subtitle != null && (
            <Box component='span' sx={{ color: colors.gray2 }}>
              {' '}
              &#8226; {subtitle}
            </Box>
          )}
        </Typography>
      </Box>
    </StyledTooltip>
  );
};

export interface ResourceGroupProps extends ColCellContentArg {
  t: TFunction;
}

export const renderAvailabilityResourceGroupLabel = ({
  groupValue,
  view,
  t,
}: ResourceGroupProps) => {
  // @ts-ignore
  const data = view?.getCurrentData();
  const vehicle = data.options.resources.find((v: any) => v.seatsRangeOrder === groupValue);

  if (vehicle?.seatsRange == null) return null;

  const [from, to] = vehicle.seatsRange.split('_').filter((el: string) => parseInt(el, 10));
  const textValue = t('SchedulerView.attributes.seatsFromTo', { from, to });

  return (
    <Typography
      sx={{
        display: 'flex',
        color: colors.headerBlack,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        textTransform: 'capitalize',
      }}
    >
      {textValue}
    </Typography>
  );
};

export const renderAvailabilityResourceHeaderContent = (labelContent: SlotLabelContentArg) => {
  const { text } = labelContent;

  return (
    <Box
      sx={{
        color: colors.gray1,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
      }}
    >
      {text}
    </Box>
  );
};
